const shopifyDomain = globalConfig.siteDomain;
const storefrontAccessToken = globalConfig.accessToken;
const graphqlEndpoint = `https://${shopifyDomain}/api/graphql`;
const DEFAULT_ALGOLIA_CLIENT_OPTIONS = { authMode: "WithinHeaders" };
const ALGOLIA_LITE_GLOBAL = "algoliasearch/lite";

function resolveAlgoliaFactory() {
  const algoliaLite = window[ALGOLIA_LITE_GLOBAL];
  if (algoliaLite && typeof algoliaLite.liteClient === "function") {
    return { type: "lite", createClient: algoliaLite.liteClient.bind(algoliaLite) };
  }

  const legacyFactory =
    typeof window.algoliasearch === "function"
      ? window.algoliasearch
      : typeof window.algoliasearch?.default === "function"
        ? window.algoliasearch.default
        : null;

  if (legacyFactory) {
    return { type: "classic", createClient: legacyFactory };
  }

  return null;
}

window.createAlgoliaLiteClient = function createAlgoliaLiteClient(appId, apiKey, options) {
  const factory = resolveAlgoliaFactory();

  if (!factory) {
    console.error("Algolia client is unavailable.");
    return null;
  }

  const explicitOptions = options || {};
  const mergedOptions =
    factory.type === "lite"
      ? {
        ...DEFAULT_ALGOLIA_CLIENT_OPTIONS,
        ...explicitOptions,
      }
      : explicitOptions;

  const args =
    factory.type === "lite"
      ? [appId, apiKey, mergedOptions]
      : Object.keys(mergedOptions).length > 0
        ? [appId, apiKey, mergedOptions]
        : [appId, apiKey];

  const client = factory.createClient(...args);

  if (
    client &&
    typeof client.multipleQueries !== "function" &&
    typeof client.search === "function"
  ) {
    const search = client.search.bind(client);
    client.multipleQueries = function multipleQueries(queries, requestOptions) {
      return search(queries, requestOptions);
    };
  }

  return client;
};

/**
 * 
 * @param {string} query 
 * @returns {{method: string; headers: Record<string, string>; body: string;}}
 */
function getGraphqlFetchOptions(query) {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
    },
    body: JSON.stringify({ query })
  }
}

/**
 * Fetches product information by Shopify product ID and returns the item's EAN  
 * along with a boolean indicating stock availability.  
 * 
 * @param {string} productId 
 * @returns {Promise<{item_ean: string; is_in_stock: boolean;}>}
 */
async function getProductInfo(productId) {
  const query = `
    query {
      product(id: "gid://shopify/Product/${productId}") {
        metafield(namespace: "facts", key: "ean") {
          value
        }
        totalInventory
      }
    }
  `;

  try {
    const response = await fetch(graphqlEndpoint, getGraphqlFetchOptions(query));
    const { data: { product: { metafield: { value: item_ean }, totalInventory } } } = await response.json();
    return {
      item_ean,
      is_in_stock: totalInventory !== 0,
    };
  } catch (error) {
    console.error(error);
    return '';
  }
}

/**
 * Retrieves a the value of a cookie by name, or null in case the cookie is not exsisting
 * 
 * @param {string} name 
 * @returns {string|null}
 */
function getCookie(name) {
  let match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  if (match) return match[2];
  return null;
}

/**
 * Retrieves the client id for the customer that is being used by shopify analytics
 * (same client id that an analytics event contains in a webpixel)
 * 
 * @returns {string|null}
 */
function getShopifyAnalyticsClientId() {
  return getCookie('_shopify_y');
}

/**
 * Stores the Algolia QueryID in localStorage for future Algolia Events
 * (Sent from GTM - this connects recommendations and searches to clicks & conversions)
 * 
 * @param {string} queryID
 * @returns {void}
 */
function setAlgoliaQueryID(queryID) {
  if (queryID) {
    localStorage.setItem('algolia_queryID', queryID);
  }
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function() {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function(event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });

    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const min = parseInt(this.input.min);
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', value <= min);
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function(fn, scope) {
  return function() {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function(selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function(target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function(path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function(country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function() {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function() {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function(e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function(selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function(selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll('button:not(.localization-selector)').forEach((button) =>
      button.addEventListener('click', this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 989px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver(entries => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));

    this.sliderControlWrapper = this.querySelector('.slider-buttons');

    if (!this.sliderControlWrapper) return;
    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');


    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach(link => link.addEventListener('click', this.linkToSlide.bind(this)));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(this.slider.clientWidth / this.sliderItemOffset);
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(new CustomEvent('slideChanged', {
        detail: {
          currentPage: this.currentPage,
          currentElement: this.sliderItemsToShow[this.currentPage - 1]
        }
      }));
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0])) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }


    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach(link => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);

  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (element.offsetLeft + element.clientWidth) <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition = event.currentTarget.name === 'next' ? this.slider.scrollLeft + (step * this.sliderItemOffset) : this.slider.scrollLeft - (step * this.sliderItemOffset);
    this.slider.scrollTo({
      left: this.slideScrollPosition
    });
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition = this.slider.scrollLeft + this.slider.clientWidth * (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.querySelector('.announcement-bar-slider')) {
      this.announcementBarArrowButtonWasClicked = false;

      this.desktopLayout = window.matchMedia('(min-width: 320px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      [this.reducedMotion, this.desktopLayout].forEach((mediaQuery) => {
        mediaQuery.addEventListener('change', () => {
          if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
        });
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener('click', () => {
          this.announcementBarArrowButtonWasClicked = true;
        }, { once: true });
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked || !this.desktopLayout.matches ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) return;

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }
    this.slider.scrollTo({
      left: this.slideScrollPosition,
    });
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked && this.desktopLayout.matches) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.querySelector('.announcement-bar-slider').contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.slider.querySelector('.slideshow__slide').clientWidth;
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }

  setSlideVisibility() {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
      (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);
    this.updatePickupAvailability();
    this.removeErrorMessage();
    this.updateVariantStatuses();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;

    const mediaGalleries = document.querySelectorAll(`[id^="MediaGallery-${this.dataset.section}"]`);
    mediaGalleries.forEach((mediaGallery) =>
      mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, true)
    );

    const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
    if (!modalContent) return;
    const newMediaModal = modalContent.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
    modalContent.prepend(newMediaModal);
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateVariantStatuses() {
    const selectedOptionOneVariants = this.variantData.filter(
      (variant) => this.querySelector(':checked').value === variant.option1
    );
    const inputWrappers = [...this.querySelectorAll('.product-form__input')];
    inputWrappers.forEach((option, index) => {
      if (index === 0) return;
      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
      const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
      const availableOptionInputsValue = selectedOptionOneVariants
        .filter((variant) => variant.available && variant[`option${index}`] === previousOptionSelected)
        .map((variantOption) => variantOption[`option${index + 1}`]);
      this.setInputAvailability(optionInputs, availableOptionInputsValue);
    });
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.innerText = input.getAttribute('value');
      } else {
        input.innerText = window.variantStrings.unavailable_with_option.replace('[value]', input.getAttribute('value'));
      }
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    const requestedVariantId = this.currentVariant.id;
    const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;

    fetch(
      `${this.dataset.url}?variant=${requestedVariantId}&section_id=${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section
      }`
    )
      .then((response) => response.text())
      .then((responseText) => {
        // prevent unnecessary ui changes from abandoned selections
        if (this.currentVariant.id !== requestedVariantId) return;

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(`price-${this.dataset.section}`);
        const source = html.getElementById(
          `price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const skuSource = html.getElementById(
          `Sku-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
        const inventorySource = html.getElementById(
          `Inventory-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);

        if (source && destination) destination.innerHTML = source.innerHTML;
        if (inventorySource && inventoryDestination) inventoryDestination.innerHTML = inventorySource.innerHTML;
        if (skuSource && skuDestination) {
          skuDestination.innerHTML = skuSource.innerHTML;
          skuDestination.classList.toggle('visibility-hidden', skuSource.classList.contains('visibility-hidden'));
        }

        const price = document.getElementById(`price-${this.dataset.section}`);

        if (price) price.classList.remove('visibility-hidden');

        if (inventoryDestination)
          inventoryDestination.classList.toggle('visibility-hidden', inventorySource.innerText === '');

        const addButtonUpdated = html.getElementById(`ProductSubmitButton-${sectionId}`);
        this.toggleAddButton(
          addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
          window.variantStrings.soldOut
        );

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId,
            html,
            variant: this.currentVariant,
          },
        });
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = window.variantStrings.addToCart;
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
    const sku = document.getElementById(`Sku-${this.dataset.section}`);

    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('visibility-hidden');
    if (inventory) inventory.classList.add('visibility-hidden');
    if (sku) sku.classList.add('visibility-hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.classList.remove('disabled');
      } else {
        input.classList.add('disabled');
      }
    });
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }
}

customElements.define('variant-radios', VariantRadios);

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);

      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement('div');
          html.innerHTML = text;
          const recommendations = html.querySelector('product-recommendations');

          if (recommendations && recommendations.innerHTML.trim().length) {
            this.innerHTML = recommendations.innerHTML;
          }

          if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
            this.remove();
          }

          if (html.querySelector('.grid__item')) {
            this.classList.add('product-recommendations--loaded');
          }
        })
        .catch((e) => {
          console.error(e);
        });
    };

    new IntersectionObserver(handleIntersection.bind(this), { rootMargin: '0px 0px 400px 0px' }).observe(this);
  }
}

customElements.define('product-recommendations', ProductRecommendations);

function pushProductListToDataLayer(items, itemListId, itemListName, offSet) {
  // Clear the previous ecommerce object
  window.dataLayer = window.dataLayer || [];

  // Construct the ecommerce object for the current list
  const ecommerceObject = {
    event: "view_item_list",
    item_list_name: itemListName,
    items: items
      .slice(offSet) // Start from the item immediately after the offset
      .map((item, index) => ({
        item_id: item['data-item_id'],
        item_ean: item['data-item_ean'],
        is_in_stock: item['data-is_in_stock'],
        item_name: item['data-item_name'],
        affiliation: item['data-affiliation'],
        price: item['data-price'],
        index: index + offSet, // Adjust index to account for the offset
        item_brand: item['data-item_brand'],
        item_category: item['data-item_category'],
        item_category2: item['data-item_category2'],
        item_list_id: itemListId,
        item_list_name: itemListName,
      })),
  };

  // Push the ecommerce object into the dataLayer
  window.dataLayer.push(ecommerceObject);
}


function setupObservers() {
  const recommendationContainers = document.querySelectorAll('.algolia-recommendation');
  console.log('containers', recommendationContainers);
  recommendationContainers.forEach(container => {
    const observer = new MutationObserver((mutations) => {
      // As soon as there's any mutation, query for current .product-card elements
      const productCards = container.querySelectorAll('.product-card');
      const items = Array.from(productCards).map((card, index) => ({
        'data-item_id': card.getAttribute('data-item_id'),
        'data-product-id': card.getAttribute('data-product-id'),
        'data-item_name': card.getAttribute('data-item_name'),
        'data-affiliation': card.getAttribute('data-affiliation'),
        'data-price': parseFloat(card.getAttribute('data-price')) || 0, // Default to 0 if NaN
        'data-item_brand': card.getAttribute('data-item_brand'),
        'data-item_category': card.getAttribute('data-item_category'),
        'data-item_category2': card.getAttribute('data-item_category2'),
        'data-index': index,
        // Extract other necessary attributes
      }));


      if (items.length > 0) {

        const productIds = items.map(item => item['data-product-id']).filter(Boolean);

        const itemListId = container.getAttribute('data-list-id');
        const itemListName = container.getAttribute('data-list-name');
        // Report the current state of items in the container
        pushProductListToDataLayer(items, itemListId, itemListName, 0);
      }
    });

    // Observe the container for child list changes
    observer.observe(container, { childList: true, subtree: true });
  });
}

function hideDisplayLoading(element) {
  if (element) {
    if (element.classList.contains('hidden')) {
      element.classList.remove('hidden');
    } else {
      element.classList.add('hidden');
    }
  } else {
    console.log("Error, element not found.");
  }
}

function addLoadingButton(element) {
  element.target.setAttribute('disabled', 'disabled');
  hideDisplayLoading(element.target.querySelector('.plp__loading'));
  element.target.classList.toggle("loading-button");
}

function revertLoadingButton(element) {
  element.target.removeAttribute('disabled');
  hideDisplayLoading(element.target.querySelector('.plp__loading'));
  element.target.classList.toggle("loading-button");
}

function disableButton(element) {
  console.log(element, "element");
  element.target.setAttribute('disabled', 'disabled');
  element.target.classList.add('plp__add--disabled');
}

function writeProductCart(productID, variantData) {
  let imageUrl = variantData?.image || '';
  let title = variantData?.displayTitle || '';
  let author = variantData?.author || '';
  const price = (variantData?.price !== undefined && variantData?.price !== null)
    ? globalConfig.currencySign + Number.parseFloat(variantData.price).toFixed(2)
    : '';
  const condition = variantData?.condition ? String(variantData.condition).toLowerCase() : '';

  // Filter & Extract data from the product
  const productDetails = document.querySelector(`div[data-product-id="${productID}"]`);

  if (productDetails) {
    const imageSelector = productDetails.querySelector('.card__inner img');
    if (!imageUrl && imageSelector) {
      imageUrl = imageSelector.src;
    }

    const titleSelector = productDetails.querySelector('.card__heading a');
    if (!title && titleSelector) {
      title = titleSelector.textContent.trim();
    }

    const authorSelector = productDetails.querySelector('.author');
    if (!author && authorSelector) {
      author = authorSelector.textContent.trim();
    }
  }

  let htmlStructure = '';
  htmlStructure += '<div class="cart-notification-product__image global-media-settings">';
  htmlStructure += imageUrl ? `<img src="${imageUrl}" alt="${title || ''}${author ? ` By ${author}` : ''}" width="100" loading="lazy">` : '';
  htmlStructure += '</div>';
  htmlStructure += '<div>';
  if (title) {
    htmlStructure += `<h3 class="cart-notification-product__name h4">${title}${author ? ` By ${author}` : ''}</h3>`;
  }
  if (condition) {
    const conditionKey = translations?.general?.cart?.option_values?.[condition] ? translations.general.cart.option_values[condition] : condition;
    htmlStructure += `<dl><div class="product-option"><dt>${translations.general.cart.option_name.condition}: </dt><dd class="cart-notification__condition">${conditionKey}</dd></div></dl>`;
  }
  if (price) {
    htmlStructure += `<div class="price-per-item"><div class="price-per-item--current"><span class="price-item-bold">${price}</span></div></div>`;
  }
  htmlStructure += '</div>';

  const target = document.getElementById('cart-notification-product');
  if (target) {
    target.innerHTML = htmlStructure;
  }
}

function updateCounterCart() {
  const cartIconBubble = document.getElementById('cart-icon-bubble');

  if (cartIconBubble.querySelector('.cart-count-bubble')) {
    const visualCounter = cartIconBubble.querySelector('.cart-count-bubble span:not(.visually-hidden)');
    const hiddenCounter = cartIconBubble.querySelector('.cart-count-bubble span.visually-hidden');
    let count = parseInt(visualCounter.innerHTML);

    visualCounter.innerHTML = count + 1;
    hiddenCounter.innerHTML = (count + 1) + (count == 1 ? ' item' : ' items');
  } else {
    const wrapper = document.createElement('div');
    wrapper.classList.add("cart-count-bubble");
    wrapper.innerHTML = '<span aria-hidden="true">1</span>'
      + '<span class="visually-hidden">1 item</span>';

    cartIconBubble.append(wrapper);
  }

  /*
  const buttonSelector = document.getElementById('cart_count');
  const visualCounter = document.querySelector('.cart-count-bubble span:not(.visually-hidden)');
  const hiddenCounter = document.querySelector('.cart-count-bubble span.visually-hidden');
  const cartCountBag = document.getElementById('cart_count_bag');

  fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        if (buttonSelector) { // Counter on the button
          buttonSelector.innerText = '(' + cart.item_count + ')';
        }

        if (visualCounter) { // General counter
          visualCounter.textContent = cart.item_count;
        }

        if (cartCountBag) { // Bag counter
          const cartCount = parseInt(cart.item_count);
          cartCountBag.innerHTML = cartCount + (cartCount === 1 ? ' item' : ' items');
        }

        if (hiddenCounter) {
          hiddenCounter.textContent = hiddenCounter.textContent.replace(/\d+/, cart.item_count);
        }
      })
      .catch(error => console.error('Error fetching cart:', error));
  */
}

async function getVariantID(productId) {
  const query = `
    query Contextualized @inContext(country: ${Shopify.country}, language: ${Shopify.locale.toUpperCase()}) {
      product(id: "gid://shopify/Product/${productId}") {
        title
        id
        metafield(namespace: "facts", key: "blocked_countries") {
          value
        }
        rrpMeta: metafield(namespace: "facts", key: "rrp") {
          value
        }
        variants(first: 12) {
          edges {
            node {
              title
              id
              availableForSale
              quantityAvailable
              priceV2 {
                amount
              }
              rrpMeta: metafield(namespace: "facts", key: "rrp") {
                value
              }
            }
          }
        }
      }
    }
  `;

  try {
    const response = await fetch(graphqlEndpoint, getGraphqlFetchOptions(query));
    const data = await response.json();

    if (data && data.data && data.data.product) {
      const product = data.data.product;
      const productRrp = product.rrpMeta?.value ? Number.parseFloat(product.rrpMeta.value) : null;
      let variants = product.variants.edges.map(edge => edge.node).filter(variant => {
        variant.id = variant.id.replace("gid://shopify/ProductVariant/", "");
        variant.price = variant.priceV2.amount;
        variant.inventory_quantity = variant.quantityAvailable;
        let variant_title = variant.title.split("/");
        variant.condition = variant_title[1].trim();
        variant.location = variant_title[0].trim();
        const vRrp = variant.rrpMeta?.value ? Number.parseFloat(variant.rrpMeta.value) : null;
        variant.rrpPrice = !Number.isNaN(vRrp) && vRrp != null ? vRrp : (productRrp || null);
        return variant;
      });

      let blockedCountries = product.metafield === null ? "" : product.metafield.value;

      if (variants.length === 0) {
        return false;
      }

      let filter_variants = variantsFilterFn(variants, countryIsoCode, marketISO, blockedCountries);
      let filter_best_variant = variantsFilterFn(filter_variants, countryIsoCode, marketISO, blockedCountries, true);
      //console.log(filter_best_variant, "filter_best_variant");
      return filter_best_variant;
    }
  } catch (error) {
    console.error('Error fetching variant ID:', error);
  }

  return null; // Return null if no matching variant is found or in case of an error
}

function parseRrp(raw, displayPrice) {
  if (raw === null || raw === undefined || raw === '') {
    return null;
  }
  let value = typeof raw === 'string'
    ? Number.parseFloat(raw.replace(/[^0-9.,]/g, '').replace(',', '.'))
    : Number.parseFloat(raw);
  if (Number.isNaN(value)) {
    return null;
  }
  const display = Number.parseFloat(displayPrice);
  if ((value > 1000 && display < 100) || (value >= 100 && display < 100 && value > (display * 5))) {
    value = value / 100;
  }
  if (!(value > 0)) {
    return null;
  }
  if (Number.isFinite(display) && display > 0 && value <= display) {
    return null;
  }
  return Math.round(value * 100) / 100;
}

function injectRRPForCard(productId, displayPrice, currencySign) {
  getVariantID(productId)
    .then((variant) => {
      if (!variant) {
        return;
      }

      const priceEl = document.querySelector(`.card[data-product-id="${productId}"] .price`);
      if (!priceEl) {
        return;
      }

      const existingRrpEl = priceEl.querySelector('.newrrp');
      const numericDisplayPrice = Number.parseFloat(displayPrice);
      const numericVariantPrice = Number.parseFloat(variant.price);
      const comparisonPrice = Number.isFinite(numericDisplayPrice) && numericDisplayPrice > 0
        ? numericDisplayPrice
        : numericVariantPrice;
      const rrp = parseRrp(variant.rrpPrice, comparisonPrice);
      const shouldShowRrp = Number.isFinite(comparisonPrice) && rrp != null && rrp > comparisonPrice;

      if (!shouldShowRrp) {
        if (existingRrpEl) {
          existingRrpEl.remove();
        }
        return;
      }

      const formattedRrp = `${currencySign}${rrp.toFixed(2)}`;
      if (existingRrpEl) {
        const rrpPriceEl = existingRrpEl.querySelector('.rrp-price');
        if (rrpPriceEl) {
          rrpPriceEl.textContent = formattedRrp;
        } else {
          existingRrpEl.innerHTML = `<span class="rrp-label">RRP</span> <s class="rrp-price">${formattedRrp}</s>`;
        }
      } else {
        const span = document.createElement('span');
        span.className = 'newrrp';
        span.innerHTML = `<span class="rrp-label">RRP</span> <s class="rrp-price">${formattedRrp}</s>`;
        priceEl.appendChild(span);
      }
    })
    .catch(() => {});
}

async function addToCart(event, variant) {
  addLoadingButton(event);
  if (!event.target.classList.contains('plp__add--disabled')) {
    let productID = event.target.getAttribute('data-product-id');

    setAlgoliaQueryID(event.target.getAttribute('data-query-id'));

    try {
      let variantDetails = variant ?? await getVariantID(productID);
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      const existingItem = cartData.items.find(item => item.id == variantDetails.id);
      const existingQty = existingItem ? existingItem.quantity : 0;

      if (variantDetails.id && existingQty < variantDetails.inventory_quantity) {
        const cartNotification = document.querySelector('cart-notification');
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantDetails.id,
            quantity: 1
          })
        })
          .then(response => {
            if (response.ok) {
              console.log('Product successfully added to cart');
              response.json().then((data) => {
                revertLoadingButton(event);
                variantDetails.image = data.image;
                variantDetails.displayTitle = data.product_title;
                const btnAuthor = event?.target?.getAttribute?.('data-author');
                if (btnAuthor) {
                  variantDetails.author = btnAuthor;
                }
                writeProductCart(productID, variantDetails);
                updateCounterCart();
                if (globalConfig.pageType == 'cart') {
                  window.location.href = globalConfig.cartUrl;
                } else {
                  if (cartNotification) cartNotification.open();
                }
              });
            } else {
              response.json().then(data => {
                revertLoadingButton(event);
                console.error('Error adding product to cart:', data);
              });
            }
          });
      } else {
        revertLoadingButton(event);
        disableButton(event);
        console.log("Error! No valid variant-id found or all products already in the cart.");
      }
    } catch (error) {
      revertLoadingButton(event);
      console.error('Error adding product to cart:', error);
    }
  }
}


// NEED TO REWRITE THIS TRACKING LISTENER
/*
document.addEventListener('DOMContentLoaded', function() {
    setupObservers();
});
*/

// Swym reload gloabl function
function swymCallbackFn(swat) {
  document.addEventListener("swym:collections-loaded", function() {
    document.querySelectorAll('.main-product-card.card-wrapper').forEach((element, index) => {
      const grandparent = element.parentElement?.parentElement;
      if (grandparent) {
        const uniqueClass = `grandparent-${index}`;
        if (globalConfig.pageType != "collection") {
          grandparent.classList.add(uniqueClass);
        }
        swat.initializeActionButtons(`.${uniqueClass}`);
      }
    });

    const allButtons = document.querySelectorAll('.swym-button.swym-add-to-wishlist-view-product');
    allButtons.forEach(button => {
      button.classList.add('is-visible');
    });
  })
}

// Swym reload PDP function
function swymCallbackFnPDP(swat) {
  document.addEventListener("swym:pdp-loaded", function() {
    swat.initializeActionButtons("#hits");
  })
}

if (!window.SwymCallbacks) {
  window.SwymCallbacks = [];
}
window.SwymCallbacks.push(swymCallbackFn);
window.SwymCallbacks.push(swymCallbackFnPDP);

//logout logic for Wishlist
var disconnect = document.querySelectorAll("a.wishlist-logout-btn, div.wishlist-logout-btn");
for (var i = 0; i < disconnect.length; i++) {
  disconnect[i].addEventListener('click', onDisconnect, false);
}

function onDisconnect(e) {
  e.preventDefault();

  // Call Swym's disconnectShopperContext function
  if (window._swat && window._swat.disconnectShopperContext) {
    window._swat.disconnectShopperContext(function(onSuccess) {
      console.log("successfully logged out", onSuccess);
    }, function(error) {
      console.log("There was an error logging you out", error);
    });
  }
}

/*
 *
 *
 * Custom JS for core features
 *
 *
 */
// Main function get filtered variants set or best option single variant

function variantsFilterFn(variantsData, isoCode, marketISO, blockedCountries, singleVariant = false) {
  // Set parameter for conditions
  const conditionPriority = ['NEW', 'LIKE_NEW', 'VERY_GOOD', 'GOOD', 'WELL_READ', 'ACCEPTABLE'];

  // Check if the isoCode is present in the blockedCountries
  if (!blockedCountries.includes(isoCode)) {
    var filteredVariants = [];
    function isAvailable(variant, _, variants) {
      const isNewBook = variant.condition === 'NEW';
      const isComingFromPLP = !variant.sku;
      if (variant.sku?.startsWith('DVD') && variant.location === 'GB' && marketISO === 'US') {
        return false;
      }

      const isInStock = variant.inventory_quantity > 0;
      const isNotZero = variant.price > 0;
      const isCondition = conditionPriority.includes(variant.condition);
      const isAllowedInRegion = isNewBook && !isComingFromPLP && !!variant.allowedRegions.find((market) => market === marketISO);
      const isHighestNewBookPriority = isNewBook && !isComingFromPLP && !variants.find(({ conditionPriority, inventory_quantity, price, allowedRegions }) => (conditionPriority[marketISO] || 20) < variant.conditionPriority[marketISO] && inventory_quantity > 0 && price > 0 && allowedRegions.includes(marketISO));

      const baseConditions = isInStock && isNotZero && isCondition;

      if (isNewBook && !isComingFromPLP) {
        return baseConditions && isAllowedInRegion && isHighestNewBookPriority;
      }

      return baseConditions;
    }

    variantsData = variantsData.filter(isAvailable);

    if (variantsData.length > 0) {
      if (singleVariant) {
        // Sort by price, then by condition
        variantsData.sort((a, b) => {
          // Sort by price in ascending order
          if (a.price !== b.price) {
            return a.price - b.price;
          }
          // If price equal, sort by conditionPriority
          const aConditionIndex = conditionPriority.indexOf(a.condition);
          const bConditionIndex = conditionPriority.indexOf(b.condition);

          // If both variants have the same conditionPriority, keep them in the same order
          if (aConditionIndex === bConditionIndex) {
            return 0;
          }
          // Otherwise, sort by conditionPriority in ascending order
          return aConditionIndex - bConditionIndex;
        });

        // Return the ID of the cheapest and best condition variant
        if (variantsData[0]) {
          const isUKMarket = (typeof marketISO !== 'undefined' ? marketISO === 'GB' : (isoCode === 'GB'));

          if (isUKMarket) {
            const usedVariants = variantsData.filter((variant) => variant.condition !== 'NEW');

            if (usedVariants.length > 0) {
              const usedConditionPriority = conditionPriority.filter((condition) => condition !== 'NEW');

              for (const condition of usedConditionPriority) {
                const bestMatch = usedVariants.find((variant) => variant.condition === condition);
                if (bestMatch) {
                  return bestMatch;
                }
              }
            }
            return variantsData[0];
          } else {
            return variantsData[0];
          }
        } else {
          return null;
        }
      } else {
        if (isoCode === 'US') {
          // Filter variants for US and missing UK stock
          variantsData.forEach(function(variant) {
            var usVersionPresent = false;
            var gbVersionPresent = false;

            conditionPriority.forEach(function(condition) {
              if (variant.title.includes('US / ' + condition)) {
                usVersionPresent = true;
                filteredVariants.push(variant);
              }
              if (variant.title.includes('GB / ' + condition)) {
                gbVersionPresent = true;
                filteredVariants.push(variant);
              }
            });

            // Remove versions with 'GB/' if a matching 'US/' version exists
            conditionPriority.forEach(function(condition) {
              var usVersionIndex = filteredVariants.findIndex(function(v) {
                return v.title.includes('US / ' + condition);
              });
              var gbVersionIndex = filteredVariants.findIndex(function(v) {
                return v.title.includes('GB / ' + condition);
              });

              if (usVersionIndex !== -1 && gbVersionIndex !== -1) {
                filteredVariants.splice(gbVersionIndex, 1);
              }
            });
          });

          var filteredVariantsTmp = [];
          conditionPriority.forEach(function(condition) {
            filteredVariants.forEach(function(variant) {
              if (condition == variant.condition) {
                filteredVariantsTmp.push(variant);
              }
            });
          });
          filteredVariants = filteredVariantsTmp;

          return filteredVariants;
        } else {
          var filteredVariantsTmp = [];
          conditionPriority.forEach(function(condition) {
            variantsData.forEach(function(variant) {
              if (condition == variant.condition) {
                filteredVariantsTmp.push(variant);
              }
            });
          });
          variantsData = filteredVariantsTmp;

          // Filter variants for UK
          return variantsData.filter(function(variant) {
            return variant.title.includes('GB');
          });
        }
      }
    } else {
      // No match after filter, return empty array
      return [];
    }
  } else {
    // If isoCode is present in blockedCountries, return empty array
    return [];
  }
}

window.onload = function() {
  const productsJson = localStorage.getItem('swym-products');
  if (productsJson) {
    const productsData = JSON.parse(productsJson);
    if (productsData.all && Array.isArray(productsData.all)) {
      productsData.all.forEach(product => {
        if (product.epi) {
          const buttons = document.querySelectorAll('.swym-button.swym-add-to-wishlist-view-product');
          buttons.forEach(button => {
            if (button.getAttribute('data-product-id') == product.empi) {
              button.classList.add('swym-added', 'swym-heart', 'swym-loaded');
            }
          });
        }
      });
    } else {
      console.error('Invalid products data format.');
    }
  }
  const allButtons = document.querySelectorAll('.swym-button.swym-add-to-wishlist-view-product');
  allButtons.forEach(button => {
    button.classList.add('is-visible');
  });
};

async function wishlistCustom(event) {
  const product_id = event.target.dataset.productId;
  if (product_id) {
    let productDetails = await getVariantID(product_id);
    if (productDetails && productDetails.id) {
      const targetElement = event.target;
      targetElement.dataset.variantId = productDetails.id;
      targetElement.dataset.swaction = "addToWishlist";
      document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
      await new Promise(resolve => setTimeout(resolve, 1000));
      targetElement.click();
    } else {
      console.log("Product details could not be found!");
      const targetElement = event.target;
      targetElement.style.opacity = "0.5";
    }
  }
}

//logout logic for Wishlist
var disconnect = document.querySelectorAll("a.wishlist-logout-btn, div.wishlist-logout-btn");
for (var i = 0; i < disconnect.length; i++) {
  disconnect[i].addEventListener('click', onDisconnect, false);
}

function onDisconnect(e) {
  e.preventDefault();

  // Call Swym's disconnectShopperContext function
  if (window._swat && window._swat.disconnectShopperContext) {
    window._swat.disconnectShopperContext(function(onSuccess) {
      console.log("successfully logged out", onSuccess);
    }, function(error) {
      console.log("There was an error logging you out", error);
    });
  }
}

// Main function to render a product card HTML from product ID
// New createProductCard (Remove the one from algolia-plp-main-collection-product-grid.js)
function renderProductCard(data, itemIndex, itemListName, lazyLoad, queryID = '') {

  const qualifyingPromotions = findQualifyingPromotions(data);

  if (!data || data === undefined) {
    return ''; // Handle the case where data is not provided or empty
  }

  const displayTitle = data.shortTitle ? data.shortTitle : data.longTitle;
  const displayAuthor = data.author ? data.author : '\u00A0';
  const isUKMarket = (typeof marketISO !== 'undefined' ? marketISO === 'GB' : (countryIsoCode === 'GB'));
  const priceSource = (isUKMarket && data.bestConditionPrice) ? data.bestConditionPrice : data.fromPrice;
  const displayPrice = parseFloat(priceSource).toFixed(2) > 0 ? parseFloat(priceSource).toFixed(2) : 0;
  const displayDisabled = displayPrice == 0 ? "disabled" : '';
  const rrpValue = parseRrp(data.rrpPrice, displayPrice);
  const isGB = countryIsoCode === 'GB';
  const rrpHtml = isGB && rrpValue && rrpValue > 0
    ? ` <span class="newrrp"><span class="rrp-label">RRP</span> <s class="rrp-price">${globalConfig.currencySign}${rrpValue.toFixed(2)}</s></span>`
    : '';

  let cardHtml = '<div class="main-product-card card-wrapper product-card-wrapper underline-links-hover swiper-slide">';
  // wishlist icon

  // @TODO: Removed Wishlist query as we will need another way
  cardHtml += '<div class="wishlist-bookmark"><div class="icon-container">';
  cardHtml += '<button onclick="wishlistCustom(event)" class="swym-button swym-heart swym-loaded swym-add-to-wishlist-view-product product_' + data.id + '" data-with-epi="true" data-swaction="" data-product-id="' + data.id + '" data-variant-id="" data-product-url="' + globalConfig.shopURL + globalConfig.localizationRoute + 'products/' + data.productHandle + '"></button>';
  cardHtml += '</div></div>';

  // Main card content
  cardHtml += '<div class="card card--standard" data-product-id="' + data.id + '">';

  // Add bindingType to use it in the cart-notification
  //cardHtml += '<span class="hidden bindingType">' + data.bindingType + '</span>';

  // Card inner content
  cardHtml += '<div class="card__inner">';

  if (data.imageURL) {
    // Image rendering logic
    if (lazyLoad) {
      cardHtml += '<img loading="lazy" src="' + data.imageURL + '" alt="' + data.shortTitle + '">';
    } else {
      cardHtml += '<img src="' + data.imageURL + '" alt="' + data.shortTitle + '">';
    }
  }

  cardHtml += '</div>'; // Close card__inner

  cardHtml += '<div class="card__content">';
  cardHtml += '<div class="card__information">';
  cardHtml += '<h3 class="card__heading h5">';

  const itemAttributes = {
    'data-item_id': data.id,
    'data-item_ean': data.isbn13,
    'data-is_in_stock': data.inStock,
    'data-item_name': data.shortTitle,
    'data-item_brand': 'WoB',
    'data-price': ((typeof marketISO !== 'undefined' ? marketISO === 'GB' : (countryIsoCode === 'GB')) && data.bestConditionPrice) ? parseFloat(data.bestConditionPrice) : parseFloat(data.fromPrice),
    'data-affiliation': 'Online Store',
    'data-item_list_name': itemListName,
    'data-index': itemIndex,
    'data-query-id': queryID,
  };

  // Convert itemAttributes object to attributes string
  const attributesString = Object.entries(itemAttributes)
    .map(([key, value]) => `${key}="${value}"`)
    .join(' ');

  cardHtml += `<a href="${globalConfig.localizationRoute}products/${data.productHandle}" onclick="handleProductClick(event);" class="full-unstyled-link product-card truncate-title" ${attributesString}>${displayTitle}</a></h3>`;
  cardHtml += `<p class="author truncate-author">${displayAuthor}</p>`
  cardHtml += `<div class="price"><div class="price-item">${globalConfig.currencySign}${displayPrice}</div>${rrpHtml}</div>`
  cardHtml += '</div>';

  // Closing tags for card inner and main content
  if (qualifyingPromotions.length >= 1) {
    cardHtml += '<div class="plp-promos-container">';
    qualifyingPromotions.map((item, index) => {
      cardHtml += `<p style="color:#${item.fontColor};background-color:#${item.color}" class="pill">${item.shortMessage}</p>`;
    })
    cardHtml += '</div>';
  }
  cardHtml += '</div>'; // Close main card content
  cardHtml += '<button ' + displayDisabled + ' type="submit" value="' + basketName + '" class="button product-form__submit plp__add" onclick="addToCart(event)" data-variant-id="" data-product-id="' + data.id + '" id="buttonID_' + data.id + '" data-query-id="' + queryID + '"> ' +
    '<span class="plp__loading hidden">' +
    '<span class="loading-overlay__spinner">' +
    '<svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">' +
    '<circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>' +
    '</svg>' +
    '</span>' +
    '</span>' + basketName + '</button>';
  cardHtml += '</div>'; // Close main card content

  // Close the main wrapper
  cardHtml += '</div>'; // Close card-wrapper product-card-wrapper underline-links-hover

  if (countryIsoCode === 'GB') {
    injectRRPForCard(data.id, Number.parseFloat(displayPrice), globalConfig.currencySign);
  }

  return cardHtml;
}

// Function to handle the click event for GTM
function handleProductClick(event) {
  //event.preventDefault();
  let target = event.currentTarget;

  setAlgoliaQueryID(target.getAttribute('data-query-id'));

  // Extract data attributes and map them to GA4 format
  let productData = {
    event: 'select_item',
    items: [{
      item_id: target.getAttribute('data-item_id'),
      item_ean: target.getAttribute('data-item_ean'),
      item_name: target.getAttribute('data-item_name'),
      item_brand: target.getAttribute('data-item_brand'),
      price: parseFloat(target.getAttribute('data-price')),
      affiliation: target.getAttribute('data-affiliation'),
      item_list_name: target.getAttribute('data-item_list_name'),
      index: parseInt(target.getAttribute('data-index')),
    }]
  };

  // Push the data to the data layer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(productData);

  console.log('%cPushed to data layer:', 'font-weight:bold; color:green;', productData);
}

// Function to handle the click event for GTM
function handleNavClick(event) {
  console.log(event);
  //event.preventDefault();
  let target = event.currentTarget;
  // Extract data attributes and map them to GA4 format
  let linkData = {
    'event': 'menu_interaction',
    'menu_category': target.getAttribute('data-menu_category'),
    'menu_subcategory': target.getAttribute('data-menu_subcategory')
  };

  // Push the data to the data layer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(linkData);

  console.log('%cPushed to data layer:', 'font-weight:bold; color:green;', linkData);
}

// Function to handle the view event for GTM
function pushViewProducts(productItems, itemListName, offset) {
  //console.log(productItems, "productItems");
  //console.log(productItems.length, "productItems.length");
  // Extract data attributes and map them to GA4 format
  let productData = {
    event: 'view_item_list',
    items: []
  };

  productItems.forEach((productItem, i) => {
    let item = {
      item_id: productItem.id,
      item_name: productItem.shortTitle ? productItem.shortTitle : productItem.longTitle,
      item_brand: 'WoB',
      price: (((typeof marketISO !== 'undefined' ? marketISO === 'GB' : (countryIsoCode === 'GB'))) && productItem.bestConditionPrice)
        ? parseFloat(productItem.bestConditionPrice)
        : parseFloat(productItem.fromPrice),
      affiliation: 'Online Store',
      item_list_name: itemListName,
      index: i + offset + 1,
    }
    //console.log(item, "item");
    productData.items.push(item);
  });

  // Push the data to the data layer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(productData);

  console.log('%cPushed to data layer:', 'font-weight:bold; color:green;', productData);
}

// Initialise SWYM event listeners
function initializeSwymEventListeners() {
  const addToWishlistEvent = 'sw:addedtowishlist';
  const removeFromWishlistEvent = 'sw:removedfromwishlist';
  const addToWishlistListener = (e) => {
    const data = e.detail.d;
    pushToDataLayer(data, 'add_to_wishlist');
  };

  const removeFromWishlistListener = (e) => {
    const data = e.detail.d;
    pushToDataLayer(data, 'remove_from_wishlist');
  };

  if (window._swat && window._swat.evtLayer) {
    window._swat.evtLayer.addEventListener(addToWishlistEvent, addToWishlistListener);
    window._swat.evtLayer.addEventListener(removeFromWishlistEvent, removeFromWishlistListener);
  }
}

// Check if SWYM is ready
function checkSwymReady() {
  if (window._swat && window._swat.evtLayer) {
    initializeSwymEventListeners();
  } else {
    setTimeout(checkSwymReady, 500); // Retry after 500ms
  }
}

// When SWYM is ready
document.addEventListener("swym:ready", function() {
  initializeSwymEventListeners();
});

// Push wishlist data to dataLayer
async function pushToDataLayer(data, eventType) {
  const { item_ean, is_in_stock } = await getProductInfo(data.empi);

  const wishlistData = {
    event: eventType,
    items: [
      {
        item_id: data.empi,
        item_ean,
        is_in_stock,
        item_sku: data.sku,
        item_name: data.dt,
        affiliation: 'Online Store',
        item_list_id: data.lid,
        item_variant: data.epi,
      }
    ]
  };

  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push(wishlistData);
  console.log(`%cPushed to data layer for ${eventType}:`, 'font-weight:bold; color:green;', wishlistData);
}

// Initial check for Swym readiness
checkSwymReady();

//set local storage for first time
if (!localStorage.recentlyViewedProducts) {
  localStorage.setItem('recentlyViewedProducts', []);
}

// Localstorage for function for Recently Viewed
function setRecentlyViewedPdp() {
  // Retrieve the existing recently viewed products from localStorage
  const storedData = localStorage.getItem('recentlyViewedProducts');
  let productArr = storedData ? JSON.parse(storedData) : [];
  // let productArr = JSON.parse(localStorage.getItem('recentlyViewedProducts')) || [];
  // Check if the product is already in the array and it's a valid ID
  if (productId && !productArr.includes(productId)) {
    // Remove the oldest item
    if (productArr.length >= 10) {
      productArr.shift();
    }
    // Add the new product ID to the array
    productArr.push(productId);
    localStorage.setItem('recentlyViewedProducts', JSON.stringify(productArr));
  }
}

// use global.object for check
if (globalConfig.pageType == "product") {
  setRecentlyViewedPdp();
}

function isBot() {
  return /bot|crawl|spider|slurp|google|bing|yandex|baidu/i.test(navigator.userAgent);
}
